import React from 'react';

import { useDispatch } from 'react-redux';

import { Link as RouterLink, useHistory } from 'react-router-dom';
import {
	Paper,
	Box,
	Button,
	Link,
	Grid,
	Typography,
	Dialog,
	DialogTitle,
	IconButton,
	DialogContent,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { Form } from 'react-final-form';
import { TextField, DatePicker } from 'mui-rff';
import DateFnsUtils from '@date-io/date-fns';

import { CountryCodeSelect } from 'components/elements';
import { APP_PATHS } from 'components/routes/paths';
import { ToggleRPC, useIsDesktop, useTranslations } from 'components/utilities';
import { INITIAL_VALUES, validate } from './helpers';
import { convertApiFormErrors } from 'helpers';
import { signUp } from 'store/actions';

// Import styles
import useStyles from './styles';

const { SIGN_IN } = APP_PATHS;

const SignUpForm = () => {
	const { t, i18n } = useTranslations();
	const dispatch = useDispatch();
	const history = useHistory();
	const classes = useStyles();

	const handleSubmit = async (values) => {
		let apiErrors = {};
		await signUp(
			values,
			(errors) => {
				apiErrors = convertApiFormErrors(errors);
			},
			(user_id, token, isSupportingSms, isSupportingCall) => {
				const newLink = APP_PATHS.VERIFICATION.replace(':id', user_id)
					.replace(':token', token)
					.replace(':isSupportingSms', isSupportingSms)
					.replace(':isSupportingCall', isSupportingCall);
				history.push(newLink);
			}
		)(dispatch);
		return apiErrors;
	};

	const direction = i18n.language === 'en' ? 'row' : 'row-reverse';

	const isDesktop = useIsDesktop();

	const boxPaddings = {
		py: isDesktop ? 6 : 3,
		px: isDesktop ? 12 : 3,
	};

	return (
		<Paper square>
			<Box {...boxPaddings}>
				<Form
					onSubmit={handleSubmit}
					validate={validate(t)}
					initialValues={INITIAL_VALUES}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit} autoComplete="off">
							<Grid
								container
								spacing={1}
								alignItems="center"
								direction={direction}
							>
								<Grid item xs={4} md={3}>
									<CountryCodeSelect
										variant="outlined"
										name="country_code"
										disabled={submitting}
									/>
								</Grid>
								<Grid item xs={8} md={9}>
									<TextField
										autoComplete="new-password"
										variant="outlined"
										label={t('register.phoneLabel')}
										name="phone"
										fullWidth
										disabled={submitting}
									/>
								</Grid>
							</Grid>

							<TextField
								autoComplete="new-password"
								type="email"
								variant="outlined"
								label={t('register.email')}
								name="email"
								fullWidth
								margin="normal"
								disabled={submitting}
							/>

							<TextField
								type="password"
								autoComplete="new-password"
								variant="outlined"
								label={t('register.password')}
								name="password"
								fullWidth
								margin="normal"
								disabled={submitting}
							/>

							<TextField
								autoComplete="new-password"
								variant="outlined"
								label={t('register.nationalId')}
								name="id_number"
								fullWidth
								margin="normal"
								disabled={submitting}
							/>

							<DatePicker
								label={t('register.dataOfBirth')}
								name="birth_date"
								openTo="year"
								disableFuture={true}
								dateFunsUtils={DateFnsUtils}
								disabled={submitting}
								margin="normal"
								inputVariant="outlined"
								format="dd.MM.yyyy"
							/>

							<DatePicker
								label={t('register.licenseExpireDate')}
								name="license_expire_date"
								openTo="year"
								dateFunsUtils={DateFnsUtils}
								disabled={submitting}
								margin="normal"
								inputVariant="outlined"
								format="dd.MM.yyyy"
							/>

							<Box pt={isDesktop ? 12 : 2}>
								<Typography variant={isDesktop ? 'h2' : 'h6'} align="center">
									{t('register.termsAndConditionLabel')}
								</Typography>
								<ToggleRPC>
									{({ on, toggle }) => (
										<>
											<Dialog
												open={on}
												onClose={toggle}
												fullWidth
												maxWidth="xl"
											>
												<DialogTitle>
													<Box display="flex" justifyContent="flex-end">
														<IconButton onClick={toggle}>
															<CancelIcon
																htmlColor="#007c73"
																fontSize={isDesktop ? 'large' : 'default'}
															/>
														</IconButton>
													</Box>
												</DialogTitle>
												<DialogContent className={classes.dialogContent}>
													<Typography
														variant="h1"
														align="center"
														gutterBottom
														className={classes.heading}
													>
														{t('policy.signUpFormModal.heading')}
													</Typography>
													<Typography
														variant="subtitle1"
														align="center"
														gutterBottom
														className={classes.paragraph}
													>
														{t('policy.signUpFormModal.content.1')}
													</Typography>
													<Typography
														variant="body1"
														align="center"
														className={classes.paragraph}
													>
														{t('policy.signUpFormModal.content.2')}
													</Typography>
												</DialogContent>
											</Dialog>
											<Link onClick={toggle}>
												<Typography
													variant={isDesktop ? 'h2' : 'h6'}
													align="center"
												>
													{t('register.termsAndConditionLink')}
												</Typography>
											</Link>
										</>
									)}
								</ToggleRPC>
							</Box>

							<Box pt={isDesktop ? 12 : 3}>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									fullWidth
									size={isDesktop ? 'large' : 'medium'}
									disabled={submitting}
									className={classes.signUpButton}
								>
									{t('register.button')}
								</Button>
							</Box>

							<Box pt={isDesktop ? 12 : 3}>
								<Typography align="center" variant={isDesktop ? 'body1' : 'h6'}>
									{t('register.signInLabel')}{' '}
									<Link component={RouterLink} to={SIGN_IN}>
										{t('register.signInLink')}
									</Link>
								</Typography>
							</Box>
						</form>
					)}
				</Form>
			</Box>
		</Paper>
	);
};

export default SignUpForm;
